import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Autocomplete
} from '@mui/material';
import { Search as SearchIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { fetchUserDashboardPermissions } from '../../redux/user/userActions';
import { gridSpacing } from 'store/constant';

// Reusable Styles
const styles = {
  // Button Styles (matching Client Onboard page)
  button: {
    base: {
      width: '9em',
      height: '3em',
      borderRadius: '30em',
      fontSize: '15px',
      fontFamily: 'inherit',
      border: 'none',
      position: 'relative',
      overflow: 'hidden',
      zIndex: 1,
      boxShadow: '6px 6px 12px rgb(255, 255, 255), -6px -6px 12px #ffffff',
      background: '#e8e8e8',
      cursor: 'pointer',
      textTransform: 'none',
      fontWeight: 500,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: '8px 8px 16px rgb(255, 255, 255), -8px -8px 16px #ffffff'
      }
    },
    small: {
      width: '6em',
      height: '2.5em',
      fontSize: '12px'
    },
    medium: {
      width: '7em',
      height: '2.5em',
      fontSize: '14px'
    },
    large: {
      width: '9em',
      height: '2.5em',
      fontSize: '15px'
    },
    colors: {
      red: {
        color: '#ef4444',
        gradient: 'linear-gradient(to right, #ef4444 0%, #dc2626 100%)'
      },
      green: {
        color: '#10b981',
        gradient: 'linear-gradient(to right, #10b981 0%, #059669 100%)'
      },
      blue: {
        color: '#0ea5e9',
        gradient: 'linear-gradient(to right, #0ea5e9 0%, #0284c7 100%)'
      }
    }
  },

  // Card Styles
  card: {
    base: {
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      borderRadius: '16px',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      height: 'fit-content',
      backdropFilter: 'blur(10px)',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)'
      }
    },
    gradients: {
      tabs: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      utc: 'linear-gradient(135deg, #fef7ed 0%, #fed7aa 100%)',
      jst: 'linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%)',
      search: 'linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%)'
    }
  },

  // Typography Styles
  typography: {
    header: {
      fontWeight: 700,
      color: '#1e293b',
      mb: 1,
      fontSize: '1.8rem',
      letterSpacing: '-0.02em'
    },
    subheader: {
      fontWeight: 600,
      color: '#1e293b',
      mb: 1,
      fontSize: '1rem',
      letterSpacing: '-0.01em'
    },
    sectionTitle: {
      fontWeight: 700,
      color: '#1e293b',
      mb: 1,
      fontSize: '0.9rem',
      textTransform: 'uppercase',
      letterSpacing: '0.05em'
    },
    body: {
      color: '#64748b',
      fontSize: '0.9rem',
      lineHeight: 1.6,
      fontWeight: 400
    },
    label: {
      color: '#374151',
      fontWeight: 500,
      fontSize: '0.9rem',
      letterSpacing: '0.01em'
    },
    labelMandatory: {
      color: '#dc2626',
      fontWeight: 700,
      fontSize: '0.9rem',
      letterSpacing: '0.01em'
    }
  },

  // Layout Styles
  layout: {
    container: {
      width: '100%',
      maxWidth: 'none',
      py: 2
    },
    panel: {
      width: '100%',
      transition: 'all 0.3s ease'
    },
    section: {
      mb: 1.5,
      p: 1.5,
      borderRadius: '8px',
      background: 'rgba(255, 255, 255, 0.9)',
      backdropFilter: 'blur(5px)',
      border: '1px solid rgba(0, 0, 0, 0.05)'
    },
    item: {
      mb: 0.5,
      p: 1,
      borderRadius: '6px',
      background: 'rgba(255, 255, 255, 0.7)',
      transition: 'all 0.2s ease',
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.9)',
        transform: 'translateX(2px)'
      }
    }
  },

  // Switch Styles
  switch: {
    size: 'medium',
    '& .MuiSwitch-thumb': {
      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
    },
    '& .MuiSwitch-track': {
      borderRadius: '12px'
    }
  },

  // Divider Styles
  divider: {
    my: 1.5,
    borderColor: 'rgba(0, 0, 0, 0.08)',
    borderWidth: '1px',
    borderRadius: '1px'
  }
};

// Reusable Button Component Factory (matching Client Onboard)
const createButton = (size = 'medium', color = 'blue', text = 'Button') => ({
  variant: 'text',
  sx: {
    ...styles.button.base,
    ...styles.button[size],
    ...styles.button.colors[color],
    '&::before': {
      content: '""',
      width: 0,
      height: styles.button[size].height,
      borderRadius: '30em',
      position: 'absolute',
      top: 0,
      left: 0,
      backgroundImage: styles.button.colors[color].gradient,
      transition: '.5s ease',
      display: 'block',
      zIndex: -1
    },
    '&:hover::before': {
      width: styles.button[size].width
    },
    '&:hover': {
      color: 'white'
    },
    '&:disabled': {
      opacity: 0.6,
      cursor: 'not-allowed'
    }
  }
});

// Reusable Card Component Factory
const createCard = (gradient = 'search') => ({
  sx: {
    ...styles.card.base,
    background: styles.card.gradients[gradient]
  }
});

// Reusable Typography Component Factory
const createTypography = (variant, style = 'body') => ({
  variant,
  sx: styles.typography[style]
});

const DashboardCustomization = ({ userDashboardPermissions, timeZone, isSkan, fetchUserDashboardPermissions, prefilledEmail = '' }) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [searchEmail, setSearchEmail] = useState('');
  const [searchedUserPermissions, setSearchedUserPermissions] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [userList, setUserList] = useState([]);
  const [userListLoading, setUserListLoading] = useState(false);
  const [emailInputValue, setEmailInputValue] = useState('');
  const [debounceTimer, setDebounceTimer] = useState(null);

  // Get current context
  const currentTimezone = timeZone || 'UTC';
  const currentDataType = isSkan ? 'skan' : 'nonskan';

  // Available tabs and widgets for customization
  const availableTabs = [
    {
      id: 'overview',
      label: 'Overview',
      description: 'Dashboard overview data',
      mandatory: true
    },
    {
      id: 'publisher_app_report',
      label: 'Publisher App Report',
      description: 'Publisher app performance data'
    },
    {
      id: 'creative_report',
      label: 'Creative Report',
      description: 'Creative performance analytics'
    },
    {
      id: 'raw_event_report',
      label: 'Raw Event Report',
      description: 'Raw event data analysis'
    },
    {
      id: 'state_level_report',
      label: 'State Level Report',
      description: 'State-level performance report'
    },
    {
      id: 'wow_report',
      label: 'WoW Report',
      description: 'Week over week analysis'
    },
    {
      id: 'dod_report',
      label: 'DoD Report',
      description: 'Day over day analysis'
    },
    {
      id: 'd2c_report',
      label: 'D2C Report',
      description: 'D2C analysis'
    }
  ];

  const availableWidgets = [
    {
      id: 'clicks',
      label: 'Clicks',
      description: 'Click tracking metrics',
      mandatory: true
    },
    {
      id: 'media_cost',
      label: 'Media Cost',
      description: 'Media cost tracking',
      mandatory: true
    },
    {
      id: 'installs',
      label: 'Installs',
      description: 'Basic install tracking',
      mandatory: true
    },
    {
      id: 'exchange_name',
      label: 'Exchange Name',
      description: 'Exchange name tracking',
      mandatory: true
    },
    {
      id: 'skan_installs',
      label: 'SKAN Installs',
      description: 'SKAN install tracking'
    },
    {
      id: 'mmp_installs',
      label: 'MMP Installs',
      description: 'MMP install tracking'
    },
    {
      id: 'impressions',
      label: 'Impressions',
      description: 'Ad impression metrics'
    },
    {
      id: 'kpi_events',
      label: 'KPI Events',
      description: 'Key performance events'
    },
    {
      id: 'mmp_events',
      label: 'MMP Events',
      description: 'MMP event tracking'
    },
    {
      id: 'skan_events',
      label: 'SKAN Events',
      description: 'SKAN event tracking'
    }
  ];

  // Widget configuration by timezone and data type
  const widgetConfig = {
    utc: {
      skan: ['impressions', 'clicks', 'media_cost', 'mmp_installs', 'mmp_events', 'skan_installs', 'skan_events', 'exchange_name'],
      non_skan: ['impressions', 'clicks', 'media_cost', 'installs', 'kpi_events', 'exchange_name']
    },
    jst: {
      skan: ['impressions', 'clicks', 'media_cost', 'skan_installs', 'skan_events'],
      non_skan: ['impressions', 'clicks', 'media_cost', 'installs', 'kpi_events', 'exchange_name']
    }
  };

  // Mandatory widgets by context
  const mandatoryWidgets = {
    utc: {
      skan: ['clicks', 'media_cost'], // Removed mmp_installs - toggle logic will handle it
      non_skan: ['clicks', 'media_cost', 'installs']
    },
    jst: {
      skan: ['clicks', 'media_cost', 'skan_installs'],
      non_skan: ['clicks', 'media_cost', 'installs']
    }
  };

  // Get current permissions - show ALL available widgets across all contexts
  const getCurrentPermissions = () => {
    // Only show permissions if we have searched for a specific user
    if (searchedUserPermissions) {
      // Extract widgets from ALL contexts and merge them
      let allWidgets = new Set();

      // Collect widgets from ALL contexts with validation
      if (searchedUserPermissions.widgets) {
        // UTC widgets
        if (searchedUserPermissions.widgets.utc_widget) {
          if (searchedUserPermissions.widgets.utc_widget.skan) {
            searchedUserPermissions.widgets.utc_widget.skan.forEach((widget) => {
              // Only add if widget is valid for UTC SKAN context
              if (widgetConfig.utc.skan.includes(widget)) {
                allWidgets.add(widget);
              } else {
                console.warn(`Invalid widget ${widget} found in UTC SKAN context, filtering out`);
              }
            });
          }
          if (searchedUserPermissions.widgets.utc_widget.non_skan) {
            searchedUserPermissions.widgets.utc_widget.non_skan.forEach((widget) => {
              // Only add if widget is valid for UTC NON-SKAN context
              if (widgetConfig.utc.non_skan.includes(widget)) {
                allWidgets.add(widget);
              } else {
                console.warn(`Invalid widget ${widget} found in UTC NON-SKAN context, filtering out`);
              }
            });
          }
        }

        // JST widgets
        if (searchedUserPermissions.widgets.jst_widget) {
          if (searchedUserPermissions.widgets.jst_widget.skan) {
            searchedUserPermissions.widgets.jst_widget.skan.forEach((widget) => {
              // Only add if widget is valid for JST SKAN context
              if (widgetConfig.jst.skan.includes(widget)) {
                allWidgets.add(widget);
              } else {
                console.warn(`Invalid widget ${widget} found in JST SKAN context, filtering out`);
              }
            });
          }
          if (searchedUserPermissions.widgets.jst_widget.non_skan) {
            searchedUserPermissions.widgets.jst_widget.non_skan.forEach((widget) => {
              // Only add if widget is valid for JST NON-SKAN context
              if (widgetConfig.jst.non_skan.includes(widget)) {
                allWidgets.add(widget);
              } else {
                console.warn(`Invalid widget ${widget} found in JST NON-SKAN context, filtering out`);
              }
            });
          }
        }
      }

      const widgetsArray = Array.from(allWidgets);

      return {
        tabs: searchedUserPermissions.tabs || [],
        widgets: widgetsArray,
        userExists: searchedUserPermissions._userExists || false
      };
    }

    // If no user is searched, show empty permissions (all toggles disabled)
    return { tabs: [], widgets: [], userExists: false };
  };

  const currentPermissions = getCurrentPermissions();

  const [localTabs, setLocalTabs] = useState([]);
  const [localWidgets, setLocalWidgets] = useState({
    utc: {
      skan: [],
      non_skan: []
    },
    jst: {
      skan: [],
      non_skan: []
    }
  });

  // Debug: Log localWidgets state changes
  useEffect(() => {
    console.log('🔍 Debug - localWidgets state changed:', localWidgets);
  }, [localWidgets]);

  // Pre-fill email when prefilledEmail prop is provided
  useEffect(() => {
    console.log('🔍 Debug - prefilledEmail received:', prefilledEmail);

    if (prefilledEmail) {
      console.log('🔍 Debug - Setting searchEmail to:', prefilledEmail);
      setSearchEmail(prefilledEmail);
    }
    // Note: Removed auto-clear logic to prevent interference with user typing
  }, [prefilledEmail]);

  // Fetch user list on component mount
  useEffect(() => {
    fetchUserList();
  }, []);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  const handleTabToggle = (tabId) => {
    // Don't allow toggling mandatory tabs
    const tab = availableTabs.find((t) => t.id === tabId);
    if (tab && tab.mandatory) {
      return;
    }

    setLocalTabs((prev) => {
      if (prev.includes(tabId)) {
        return prev.filter((id) => id !== tabId);
      } else {
        return [...prev, tabId];
      }
    });
  };

  const handleWidgetToggle = (widgetId, timezone, dataType) => {
    // Validate widget is allowed in this context
    if (!widgetConfig[timezone] || !widgetConfig[timezone][dataType] || !widgetConfig[timezone][dataType].includes(widgetId)) {
      console.warn(`Widget ${widgetId} is not allowed in ${timezone} ${dataType} context`);
      return;
    }

    // Don't allow toggling mandatory widgets
    const isMandatory =
      mandatoryWidgets[timezone] && mandatoryWidgets[timezone][dataType] && mandatoryWidgets[timezone][dataType].includes(widgetId);
    if (isMandatory) {
      return;
    }

    setLocalWidgets((prev) => {
      const newWidgets = { ...prev };
      const currentList = newWidgets[timezone][dataType];
      const isCurrentlyEnabled = currentList.includes(widgetId);

      // Handle MMP Installs and SKAN Installs - prevent both from being disabled
      if (widgetId === 'mmp_installs' || widgetId === 'skan_installs') {
        const otherInstallWidget = widgetId === 'mmp_installs' ? 'skan_installs' : 'mmp_installs';
        const otherIsEnabled = currentList.includes(otherInstallWidget);

        if (isCurrentlyEnabled) {
          // Trying to disable - only allow if the other install widget is enabled
          if (otherIsEnabled) {
            newWidgets[timezone][dataType] = currentList.filter((id) => id !== widgetId);
          }
          // If other is not enabled, don't allow disabling (prevent both from being disabled)
        } else {
          // Enabling - just add it
          newWidgets[timezone][dataType] = [...currentList, widgetId];
        }
      } else {
        // Regular toggle for other widgets
        if (isCurrentlyEnabled) {
          newWidgets[timezone][dataType] = currentList.filter((id) => id !== widgetId);
        } else {
          newWidgets[timezone][dataType] = [...currentList, widgetId];
        }
      }

      console.log(`🔍 Debug - Widget toggled: ${widgetId} in ${timezone} ${dataType}`);
      console.log(`🔍 Debug - New state:`, newWidgets[timezone][dataType]);

      return newWidgets;
    });
  };

  // Debounced auto-search function
  // Fetch user list from API
  const fetchUserList = async () => {
    setUserListLoading(true);
    try {
      const response = await fetch('/get_all_users_list', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        // Clean the JSON string by replacing NaN values with null before parsing
        const cleanedData = result.data.replace(/:\s*NaN/g, ': null');
        const userData = JSON.parse(cleanedData);
        setUserList(userData);
      } else {
        setMessage('Failed to load user list');
        setMessageType('error');
      }
    } catch (error) {
      console.error('Error fetching user list:', error);
      setMessage('Failed to load user list');
      setMessageType('error');
    } finally {
      setUserListLoading(false);
    }
  };

  const debouncedSearch = (email) => {
    // Clear existing timer
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    // Set new timer
    const newTimer = setTimeout(() => {
      if (email && email.trim()) {
        // Check if email exists in user list
        const emailExists = userList.some((user) => user.email_id === email.trim());
        if (emailExists) {
          handleSearchUser(email);
        } else {
          // Don't show error immediately - let user continue typing to filter
          // Only show error if they try to search with a non-existent email
        }
      } else if (email === '') {
        // Clear search if email is empty
        handleClearSearch();
      }
    }, 800); // 800ms delay

    setDebounceTimer(newTimer);
  };

  const handleSearchUser = async (email = searchEmail) => {
    if (!email || !email.trim()) {
      setMessage('Please enter a valid email address to search for user permissions');
      setMessageType('error');
      return;
    }

    setSearchLoading(true);
    setMessage('');

    try {
      const response = await fetch(`/get_user_dashboard_permission_customization?email=${encodeURIComponent(email.trim())}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Mark user as existing
        const userData = { ...data, _userExists: true };
        setSearchedUserPermissions(userData);

        // Filter tabs to only include those available in UI
        const availableTabIds = availableTabs.map((tab) => tab.id);
        const filteredTabs = (data.tabs || []).filter((tabId) => availableTabIds.includes(tabId));

        // Add mandatory tabs to the filtered tabs
        const mandatoryTabs = availableTabs.filter((tab) => tab.mandatory).map((tab) => tab.id);
        const allTabs = [...new Set([...filteredTabs, ...mandatoryTabs])];
        setLocalTabs(allTabs);

        // Extract widgets from API response and organize by timezone and data type
        const newWidgets = {
          utc: {
            skan: [],
            non_skan: []
          },
          jst: {
            skan: [],
            non_skan: []
          }
        };

        // Debug: Log the raw widgets data
        console.log('API widgets data:', data.widgets);

        if (data.widgets) {
          // UTC widgets
          if (data.widgets.utc_widget) {
            if (data.widgets.utc_widget.skan) {
              newWidgets.utc.skan = data.widgets.utc_widget.skan.filter((widget) => availableWidgets.some((w) => w.id === widget));
            }
            if (data.widgets.utc_widget.non_skan) {
              newWidgets.utc.non_skan = data.widgets.utc_widget.non_skan.filter((widget) => availableWidgets.some((w) => w.id === widget));
            }
          }

          // JST widgets
          if (data.widgets.jst_widget) {
            if (data.widgets.jst_widget.skan) {
              newWidgets.jst.skan = data.widgets.jst_widget.skan.filter((widget) => availableWidgets.some((w) => w.id === widget));
            }
            if (data.widgets.jst_widget.non_skan) {
              newWidgets.jst.non_skan = data.widgets.jst_widget.non_skan.filter((widget) => availableWidgets.some((w) => w.id === widget));
            }
          }
        }

        // Add mandatory widgets to each section based on context
        Object.keys(newWidgets).forEach((timezone) => {
          Object.keys(newWidgets[timezone]).forEach((dataType) => {
            // Get mandatory widgets for this specific context
            const contextMandatoryWidgets =
              mandatoryWidgets[timezone] && mandatoryWidgets[timezone][dataType] ? mandatoryWidgets[timezone][dataType] : [];

            // Add context-specific mandatory widgets
            newWidgets[timezone][dataType] = [...new Set([...newWidgets[timezone][dataType], ...contextMandatoryWidgets])];

            // Validate all widgets are allowed in this context
            newWidgets[timezone][dataType] = newWidgets[timezone][dataType].filter(
              (widget) => widgetConfig[timezone] && widgetConfig[timezone][dataType] && widgetConfig[timezone][dataType].includes(widget)
            );
          });
        });

        console.log('Extracted widgets by context:', newWidgets);
        setLocalWidgets(newWidgets);
        setMessage(`Successfully loaded customizations for ${email}`);
        setMessageType('success');
      } else if (response.status === 404) {
        // Mark user as not existing
        const userData = { tabs: [], widgets: [], _userExists: false };
        setSearchedUserPermissions(userData);

        // Initialize local state with mandatory items for new user
        const mandatoryTabs = availableTabs.filter((tab) => tab.mandatory).map((tab) => tab.id);
        const mandatoryWidgets = availableWidgets.filter((widget) => widget.mandatory).map((widget) => widget.id);

        // Initialize widgets structure for new user
        const defaultWidgets = {
          utc: {
            skan: [...mandatoryWidgets, 'mmp_installs'],
            non_skan: [...mandatoryWidgets]
          },
          jst: {
            skan: [...mandatoryWidgets, 'skan_installs'],
            non_skan: [...mandatoryWidgets]
          }
        };

        setLocalTabs(mandatoryTabs);
        setLocalWidgets(defaultWidgets);
        setMessage(`No customizations found for ${email} - ready to customize dashboard`);
        setMessageType('info');
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Search user error:', error);
      if (error.message.includes('404') || error.message.includes('Not Found')) {
        setMessage(`User not found: ${email} - This email does not exist in the system`);
      } else if (error.message.includes('400') || error.message.includes('Bad Request')) {
        setMessage(`Invalid email format: ${email} - Please check the email address`);
      } else if (error.message.includes('500') || error.message.includes('Internal Server Error')) {
        setMessage(`Server error: Unable to search for user - Please try again later`);
      } else {
        setMessage(`Failed to search user: ${error.message}`);
      }
      setMessageType('error');
      setSearchedUserPermissions(null);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleClearSearch = () => {
    console.log('🔍 Debug - handleClearSearch called');
    setSearchEmail('');
    setSearchedUserPermissions(null);
    setLocalTabs([]);
    setLocalWidgets({
      utc: {
        skan: [],
        non_skan: []
      },
      jst: {
        skan: [],
        non_skan: []
      }
    });
    setMessage('Search cleared - ready for new search');
    setMessageType('info');
  };

  const handleSaveChanges = async () => {
    if (!searchEmail.trim()) {
      setMessage('Please search for a user first before saving changes');
      setMessageType('error');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      // Smart detection: Start with PUT (update) and fallback to POST (create) if 404
      let method = 'PUT';
      let endpoint = 'update_user_dashboard_permission';

      // Build request body based on selected toggles - context-aware structure
      const requestBody = {};

      // Filter tabs to only include those available in UI
      const availableTabIds = availableTabs.map((tab) => tab.id);
      const filteredTabs = localTabs.filter((tabId) => availableTabIds.includes(tabId));

      // Add tabs if any are selected
      if (filteredTabs.length > 0) {
        requestBody.tabs = filteredTabs;
      }

      // Convert nested widget structure to the format expected by backend
      const widgetsForBackend = {
        utc_widget: {
          skan: localWidgets.utc.skan,
          non_skan: localWidgets.utc.non_skan
        },
        jst_widget: {
          skan: localWidgets.jst.skan,
          non_skan: localWidgets.jst.non_skan
        }
      };

      // Debug logging
      console.log('🔍 Debug - localWidgets state:', localWidgets);
      console.log('🔍 Debug - widgetsForBackend:', widgetsForBackend);

      // Add widgets - backend expects nested structure
      requestBody.widgets = widgetsForBackend;

      // Send the request body with only enabled items
      const body = requestBody;
      const bodyString = JSON.stringify(body);

      console.log('🔍 Debug - Final payload:', body);

      let response = await fetch(`/${endpoint}?email=${encodeURIComponent(searchEmail.trim())}`, {
        method: method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: bodyString
      });

      let responseData = await response.json();

      // Smart fallback: If PUT returns 404, automatically try POST (create)
      if (!response.ok && response.status === 404 && method === 'PUT') {
        console.log('🔄 User not found with PUT, trying POST (create) instead...');
        method = 'POST';
        endpoint = 'create_user_dashboard_permission';

        response = await fetch(`/${endpoint}?email=${encodeURIComponent(searchEmail.trim())}`, {
          method: method,
          headers: {
            'Content-Type': 'application/json'
          },
          body: bodyString
        });

        responseData = await response.json();
      }

      if (response.ok) {
        const action = method === 'PUT' ? 'updated' : 'created';
        setMessage(`Permissions ${action} successfully for ${searchEmail}`);
        setMessageType('success');
        // Refresh the search to show updated permissions
        await handleSearchUser();
      } else {
        throw new Error(`HTTP ${response.status}: ${responseData.error || response.statusText}`);
      }
    } catch (error) {
      setMessage(`Error saving permissions: ${error.message}`);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePermissions = async () => {
    if (!searchEmail.trim()) {
      setMessage('Please search for a user first before deleting permissions');
      setMessageType('error');
      return;
    }

    if (!searchedUserPermissions || Object.keys(searchedUserPermissions).length === 0) {
      setMessage('No existing permissions found to delete');
      setMessageType('error');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const response = await fetch(`/delete_user_dashboard_permission?email=${encodeURIComponent(searchEmail.trim())}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setMessage(`Permissions deleted successfully for ${searchEmail}`);
        setMessageType('success');
        // Clear the search and reset local state
        setSearchedUserPermissions(null);
        setLocalTabs([]);
        setLocalWidgets({
          utc: {
            skan: [],
            non_skan: []
          },
          jst: {
            skan: [],
            non_skan: []
          }
        });
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      setMessage(`Error deleting permissions: ${error.message}`);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const isTabEnabled = (tabId) => {
    return localTabs.includes(tabId);
  };

  const isWidgetEnabled = (widgetId, timezone, dataType) => {
    return localWidgets[timezone][dataType].includes(widgetId);
  };

  const isWidgetDisabled = (widgetId, timezone, dataType) => {
    // Check if widget is mandatory for this specific context
    const isMandatory =
      mandatoryWidgets[timezone] && mandatoryWidgets[timezone][dataType] && mandatoryWidgets[timezone][dataType].includes(widgetId);

    if (isMandatory) {
      return true;
    }

    // For MMP Installs and SKAN Installs, disable if trying to disable the last enabled one
    if (widgetId === 'mmp_installs' || widgetId === 'skan_installs') {
      const isCurrentlyEnabled = localWidgets[timezone][dataType].includes(widgetId);
      const otherInstallWidget = widgetId === 'mmp_installs' ? 'skan_installs' : 'mmp_installs';
      const otherIsEnabled = localWidgets[timezone][dataType].includes(otherInstallWidget);

      // Disable if trying to disable the last enabled install widget
      return isCurrentlyEnabled && !otherIsEnabled;
    }

    return false;
  };

  // Common button styles - declared once and reused
  const buttonStyles = {
    width: '9em',
    height: '3em',
    borderRadius: '30em',
    fontSize: '15px',
    fontFamily: 'inherit',
    border: 'none',
    position: 'relative',
    overflow: 'hidden',
    zIndex: 1,
    boxShadow: '6px 6px 12px rgb(255, 255, 255), -6px -6px 12px #ffffff',
    background: '#e8e8e8',
    cursor: 'pointer',
    textTransform: 'none',
    fontWeight: 500,
    color: '#090909',
    '&::before': {
      content: '""',
      width: 0,
      height: '3em',
      borderRadius: '30em',
      position: 'absolute',
      top: 0,
      left: 0,
      backgroundImage: 'linear-gradient(to right, rgb(15, 203, 216) 0%, rgb(71, 154, 249) 100%)',
      transition: '.5s ease',
      display: 'block',
      zIndex: -1
    },
    '&:hover::before': {
      width: '9em'
    },
    '&:disabled': {
      opacity: 0.6,
      cursor: 'not-allowed'
    }
  };

  return (
    <>
      <Grid container spacing={gridSpacing} sx={styles.layout.container}>
        <Grid item xs={12}>
          <Box
            sx={{
              textAlign: 'center',
              margin: '-16px 300px 20px',
              padding: '20px',
              background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
              borderRadius: '8px',
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.06)',
              border: '1px solid rgba(0, 0, 0, 0.05)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: '#1e293b',
                mb: 0.5,
                position: 'relative',
                zIndex: 1
              }}
            >
              Dashboard Customization
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#64748b',
                fontWeight: 400,
                position: 'relative',
                zIndex: 1
              }}
            >
              Customize user dashboard and visibility across timezones
            </Typography>
          </Box>

          {/* Email Search Section */}
          <Card {...createCard('search')} sx={{ mb: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={styles.typography.subheader}>
                  User Customizations
                </Typography>
                <Typography variant="body2" sx={styles.typography.body}>
                  Enter user email address - Customizations will auto-load as you type
                </Typography>
              </Box>

              <Grid container spacing={2}>
                {/* Left Half - Search */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Autocomplete
                      fullWidth
                      options={userList}
                      getOptionLabel={(option) => option.email_id || option}
                      value={searchEmail}
                      inputValue={emailInputValue}
                      filterOptions={(options, { inputValue }) => {
                        if (!inputValue) return options.slice(0, 10); // Show first 10 if no input

                        const lowerInput = inputValue.toLowerCase();
                        const filtered = options.filter((option) => {
                          const email = (option.email_id || '').toLowerCase();
                          return (
                            email.startsWith(lowerInput) ||
                            email.split('@')[0].startsWith(lowerInput) ||
                            email.split('@')[1]?.startsWith(lowerInput)
                          );
                        });

                        if (filtered.length > 0) {
                          return filtered.slice(0, 10).sort((a, b) => {
                            const emailA = (a.email_id || '').toLowerCase();
                            const emailB = (b.email_id || '').toLowerCase();
                            return emailA.localeCompare(emailB);
                          });
                        }

                        // Fallback to contains matches if no starts-with matches
                        const containsMatches = options
                          .filter((option) => {
                            const email = (option.email_id || '').toLowerCase();
                            return email.includes(lowerInput);
                          })
                          .slice(0, 10);

                        return containsMatches.sort((a, b) => {
                          const emailA = (a.email_id || '').toLowerCase();
                          const emailB = (b.email_id || '').toLowerCase();
                          return emailA.localeCompare(emailB);
                        });
                      }}
                      onInputChange={(event, newInputValue) => {
                        setEmailInputValue(newInputValue);
                        setSearchEmail(newInputValue);
                        debouncedSearch(newInputValue);
                      }}
                      onChange={(event, newValue) => {
                        if (newValue) {
                          const email = typeof newValue === 'string' ? newValue : newValue.email_id;
                          setSearchEmail(email);
                          setEmailInputValue(email);
                          handleSearchUser(email);
                        }
                      }}
                      loading={userListLoading}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="User Email"
                          placeholder="Select or type user email address"
                          variant="outlined"
                          size="medium"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              backgroundColor: 'white',
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#0ea5e9'
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#0ea5e9',
                                borderWidth: 2
                              }
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                              color: '#0ea5e9'
                            }
                          }}
                        />
                      )}
                    />
                    {searchedUserPermissions && (
                      <Button onClick={handleClearSearch} {...createButton('small', 'red')}>
                        Clear
                      </Button>
                    )}
                  </Box>
                </Grid>

                {/* Right Half - Widget Notes */}
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      p: 1.5,
                      backgroundColor: '#f8fafc',
                      borderRadius: 2,
                      border: '1px solid #e2e8f0',
                      height: '100%'
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 1.5,
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        color: '#1e293b'
                      }}
                    >
                      Notes
                    </Typography>
                    <Box
                      sx={{
                        fontSize: '0.75rem',
                        color: '#64748b',
                        lineHeight: 1.4
                      }}
                    >
                      <Typography variant="caption" sx={{ display: 'block', mb: 0.5, pl: 1 }}>
                        • <strong>MMP Installs</strong> ↔ <strong>SKAN Installs</strong>: At least one must be enabled
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', mb: 1, pl: 1 }}>
                        • <strong>Mandatory Viwes</strong> cannot be disabled
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', mb: 1, pl: 1 }}>
                        • <strong>Default Viwes : </strong> All tabs and views will be enabled
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {message && (
            <Alert
              severity={messageType}
              sx={{
                mb: 3,
                borderRadius: 3,
                fontSize: '0.95rem',
                fontWeight: 500,
                boxShadow:
                  messageType === 'success'
                    ? '0 8px 24px rgba(76, 175, 80, 0.12)'
                    : messageType === 'error'
                    ? '0 8px 24px rgba(244, 67, 54, 0.12)'
                    : '0 8px 24px rgba(33, 150, 243, 0.12)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                '& .MuiAlert-icon': {
                  fontSize: '1.25rem'
                },
                '& .MuiAlert-message': {
                  fontWeight: 500
                }
              }}
            >
              {message}
            </Alert>
          )}

          <Box sx={{ width: '100%' }}>
            <Grid container spacing={2} sx={{ width: '100%' }}>
              {/* Tabs Section */}
              <Grid item xs={12} lg={4} md={12}>
                <Card {...createCard('tabs')}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ mb: 2, textAlign: 'center' }}>
                      <Typography variant="h6" sx={styles.typography.subheader}>
                        Dashboard Tabs
                      </Typography>
                      <Typography variant="body2" sx={styles.typography.body}>
                        Control which tabs are visible in the sidebar navigation
                      </Typography>
                    </Box>

                    <Box>
                      {availableTabs.map((tab) => (
                        <Box key={tab.id} sx={styles.layout.item}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={isTabEnabled(tab.id)}
                                onChange={() => handleTabToggle(tab.id)}
                                disabled={loading || tab.mandatory}
                                size={styles.switch.size}
                              />
                            }
                            label={
                              <Typography variant="body2" sx={tab.mandatory ? styles.typography.labelMandatory : styles.typography.label}>
                                {tab.label}
                              </Typography>
                            }
                          />
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* UTC Widgets Section */}
              <Grid item xs={12} lg={4} md={6}>
                <Card {...createCard('utc')}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ mb: 2, textAlign: 'center' }}>
                      <Typography variant="h6" sx={styles.typography.subheader}>
                        UTC Views
                      </Typography>
                      <Typography variant="body2" sx={styles.typography.body}>
                        Control which views are visible for UTC timezone
                      </Typography>
                    </Box>

                    {/* UTC NON-SKAN Section */}
                    <Box sx={styles.layout.section}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          ...styles.typography.sectionTitle,
                          textAlign: 'center'
                        }}
                      >
                        ANDROID
                      </Typography>

                      <Box>
                        {availableWidgets
                          .filter((widget) => widgetConfig.utc.non_skan.includes(widget.id))
                          .map((widget) => {
                            const isMandatory = mandatoryWidgets.utc.non_skan.includes(widget.id);
                            return (
                              <Box key={`utc-nonskan-${widget.id}`} sx={styles.layout.item}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={isWidgetEnabled(widget.id, 'utc', 'non_skan')}
                                      onChange={() => handleWidgetToggle(widget.id, 'utc', 'non_skan')}
                                      disabled={loading || isWidgetDisabled(widget.id, 'utc', 'non_skan')}
                                      size={styles.switch.size}
                                    />
                                  }
                                  label={
                                    <Typography
                                      variant="body2"
                                      sx={isMandatory ? styles.typography.labelMandatory : styles.typography.label}
                                    >
                                      {widget.label} {isMandatory && '(Mandatory)'}
                                    </Typography>
                                  }
                                />
                              </Box>
                            );
                          })}
                      </Box>
                    </Box>

                    <Divider sx={styles.divider} />

                    {/* UTC SKAN Section */}
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          ...styles.typography.sectionTitle,
                          textAlign: 'center'
                        }}
                      >
                        IOS
                      </Typography>

                      <Box>
                        {availableWidgets
                          .filter((widget) => widgetConfig.utc.skan.includes(widget.id))
                          .map((widget) => {
                            const isMandatory = mandatoryWidgets.utc.skan.includes(widget.id);
                            return (
                              <Box key={`utc-skan-${widget.id}`} sx={styles.layout.item}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={isWidgetEnabled(widget.id, 'utc', 'skan')}
                                      onChange={() => handleWidgetToggle(widget.id, 'utc', 'skan')}
                                      disabled={loading || isWidgetDisabled(widget.id, 'utc', 'skan')}
                                      size={styles.switch.size}
                                    />
                                  }
                                  label={
                                    <Typography
                                      variant="body2"
                                      sx={isMandatory ? styles.typography.labelMandatory : styles.typography.label}
                                    >
                                      {widget.label} {isMandatory && '(Mandatory)'}
                                    </Typography>
                                  }
                                />
                              </Box>
                            );
                          })}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* JST Widgets Section */}
              <Grid item xs={12} lg={4} md={6}>
                <Card {...createCard('jst')}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ mb: 2, textAlign: 'center' }}>
                      <Typography variant="h6" sx={styles.typography.subheader}>
                        JST Views
                      </Typography>
                      <Typography variant="body2" sx={styles.typography.body}>
                        Control which views are visible for JST timezone
                      </Typography>
                    </Box>

                    {/* JST NON-SKAN Section */}
                    <Box sx={styles.layout.section}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          ...styles.typography.sectionTitle,
                          textAlign: 'center'
                        }}
                      >
                        NON-SKAN
                      </Typography>

                      <Box>
                        {availableWidgets
                          .filter((widget) => widgetConfig.jst.non_skan.includes(widget.id))
                          .map((widget) => {
                            const isMandatory = mandatoryWidgets.jst.non_skan.includes(widget.id);
                            return (
                              <Box key={`jst-nonskan-${widget.id}`} sx={styles.layout.item}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={isWidgetEnabled(widget.id, 'jst', 'non_skan')}
                                      onChange={() => handleWidgetToggle(widget.id, 'jst', 'non_skan')}
                                      disabled={loading || isWidgetDisabled(widget.id, 'jst', 'non_skan')}
                                      size={styles.switch.size}
                                    />
                                  }
                                  label={
                                    <Typography
                                      variant="body2"
                                      sx={isMandatory ? styles.typography.labelMandatory : styles.typography.label}
                                    >
                                      {widget.label} {isMandatory && '(Mandatory)'}
                                    </Typography>
                                  }
                                />
                              </Box>
                            );
                          })}
                      </Box>
                    </Box>

                    <Divider sx={styles.divider} />

                    {/* JST SKAN Section */}
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          ...styles.typography.sectionTitle,
                          textAlign: 'center'
                        }}
                      >
                        SKAN
                      </Typography>

                      <Box>
                        {availableWidgets
                          .filter((widget) => widgetConfig.jst.skan.includes(widget.id))
                          .map((widget) => {
                            const isMandatory = mandatoryWidgets.jst.skan.includes(widget.id);
                            return (
                              <Box key={`jst-skan-${widget.id}`} sx={styles.layout.item}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={isWidgetEnabled(widget.id, 'jst', 'skan')}
                                      onChange={() => handleWidgetToggle(widget.id, 'jst', 'skan')}
                                      disabled={loading || isWidgetDisabled(widget.id, 'jst', 'skan')}
                                      size={styles.switch.size}
                                    />
                                  }
                                  label={
                                    <Typography
                                      variant="body2"
                                      sx={isMandatory ? styles.typography.labelMandatory : styles.typography.label}
                                    >
                                      {widget.label} {isMandatory && '(Mandatory)'}
                                    </Typography>
                                  }
                                />
                              </Box>
                            );
                          })}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Actions */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'flex-end',
              mt: 3,
              p: 2
            }}
          >
            <Button
              onClick={handleDeletePermissions}
              disabled={loading || !searchEmail.trim() || !searchedUserPermissions}
              {...createButton('medium', 'red')}
            >
              {loading ? <CircularProgress size={16} color="inherit" /> : 'Delete'}
            </Button>
            <Button disabled={loading || !searchEmail.trim()} onClick={handleSaveChanges} {...createButton('large', 'green')}>
              {loading ? <CircularProgress size={16} color="inherit" /> : 'Save Changes'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

const mapStateToProps = (state) => ({
  userDashboardPermissions: state.userReducer.userDashboardPermissions,
  timeZone: state.advertiserReducer.timeZone,
  isSkan: state.campaignReducer.isSkan
});

const mapDispatchToProps = {
  fetchUserDashboardPermissions
};

export default connect(mapStateToProps, mapDispatchToProps)(DashboardCustomization);
